'use client';
import { useEffect, useState } from 'react';
import { cn } from '@/app/utils/cn';

/**
 * SvgIcon Component
 *
 * Fetches and renders SVG content from DotCMS via the /api/svg route (avoids CORS).
 * Sanitization is performed server-side before the content reaches the client.
 * Allows customization of SVG colors through className.
 *
 * @param {Object} props
 * @param {string|Object} props.icon - Can be:
 *   - String identifier: "abc123" (resolved to /dA/<id>)
 *   - String path: "/path/to/icon.svg" or "https://..." (passed through as-is)
 *   - Object: { identifier: "abc123" } or { idPath: "/path" } or { fileAsset: { idPath: "/path" } }
 * @param {string} props.className - Tailwind classes for styling (colors will be applied to SVG)
 * @param {number} props.size - Size in pixels (default: 24)
 */
export function SvgIcon({ icon, className = '', size = 24 }) {
    const [svgContent, setSvgContent] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    let iconSource = null;

    if (typeof icon === 'string') {
        iconSource = icon;
    } else if (icon?.fileAsset?.idPath) {
        iconSource = icon.fileAsset.idPath;
    } else if (icon?.idPath) {
        iconSource = icon.idPath;
    } else if (icon?.identifier) {
        iconSource = icon.identifier;
    }

    useEffect(() => {
        if (!iconSource) {
            setIsLoading(false);
            return;
        }

        // Inline SVG markup passed directly by callers (e.g. previews, fixtures).
        // Trusted source only — bypasses the sanitizing /api/svg proxy.
        if (iconSource.trimStart().startsWith('<svg')) {
            setSvgContent(iconSource);
            setIsLoading(false);
            setHasError(false);
            return;
        }

        setIsLoading(true);
        setHasError(false);

        const url = `/api/svg?source=${encodeURIComponent(iconSource)}`;

        fetch(url)
            .then((res) => res.json())
            .then((result) => {
                if (result.success) {
                    setSvgContent(result.content);
                } else {
                    setHasError(true);
                }
                setIsLoading(false);
            })
            .catch((err) => {
                setHasError(true);
                setIsLoading(false);
            });
    }, [iconSource]);

    if (isLoading) {
        return (
            <div
                className={cn('animate-pulse rounded-full bg-white/20', className)}
                style={{ width: size, height: size }}
            />
        );
    }

    if (hasError) {
        return (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={cn('lucide lucide-circle-alert', className)}>
                <circle cx="12" cy="12" r="10" />
                <line x1="12" x2="12" y1="8" y2="12" />
                <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
        );
    }

    if (!svgContent) return null;

    // Modify only the opening <svg> tag to use the specified size
    let modifiedSvg = svgContent
        .replace(/(<svg[^>]*?)\s+width=["'][^"']*["']/i, '$1')
        .replace(/(<svg[^>]*?)\s+height=["'][^"']*["']/i, '$1');

    modifiedSvg = modifiedSvg.replace(/(<svg[^>]*?)>/i, `$1 width="${size}" height="${size}">`);

    return <div className={className} dangerouslySetInnerHTML={{ __html: modifiedSvg }} />;
}
